import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { ROUTE } from '@/routes/constants';
import { CompetitionRulesDialog } from '@/components/competition-rules-dialog';
import { Theme, ThemeColors, THEME, THEME_ORDER, DECOR_COLORS, DecorKey } from './login-theme';
// 主题轮盘：走打包导入(带 hash)避免部署环境 public 绝对路径破图(与 login.tsx 一致)
import themeWheel from '@/assets/images/theme-wheel.svg';

// 手机横屏(844×390)登录/注册/重置 三态共享的画布外壳 —— 坐标 1:1 取自独立设计稿
// frame(w137lnJnpJGrOyWZGV9cnT:551-988)。该稿与 1920 桌面稿是两套不同构图(卡片近正方/字号更小/
// 顶栏一行),故单独一套坐标,不复用桌面 1920 画布。整块按 min(vw/844, vh/390) 等比缩放居中(FitScale),
// 844×390 处 scale=1 逐像素。右侧表单由各页经 children 传入,自行在舞台内 absolute 定位。

const STG_W = 844;
const STG_H = 390;
const CARD = { left: 12, top: 12, width: 369, height: 366, radius: 30 };

// 装饰(卡片相对坐标 = 设计稿 frame 坐标 − 卡片原点(12,12));卡片 overflow:hidden 负责裁切溢出部分
const DECOR: { key: DecorKey; left: number; top: number; width: number; height: number }[] = [
  { key: 'cap',   left: 216, top: 80,  width: 100, height: 93 },
  { key: 'dots',  left: 154, top: 209, width: 39,  height: 48 },
  { key: 'a',     left: -8,  top: 247, width: 99,  height: 101 },
  { key: 'd',     left: 99,  top: 238, width: 120, height: 120 },
  { key: 'at',    left: 210, top: 241, width: 113, height: 112 },
  { key: 'quote', left: 324, top: 235, width: 45,  height: 45 },
];

interface Props {
  theme: Theme;
  setTheme: (t: Theme) => void;
  colors: ThemeColors;
  children: React.ReactNode;
}

export default function MobileLandscapeAuthShell({ theme, setTheme, colors, children }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // FitScale：844×390 舞台等比缩放贴合视口(取宽/高较小比例,居中,letterbox)
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const update = () => setScale(Math.min(window.innerWidth / STG_W, window.innerHeight / STG_H));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // 语言切换
  const current = Cookies.get('i18next') || i18n.language || 'zh-HK';
  const isEn = current.startsWith('en');
  const setLang = (lng: 'en' | 'zh-HK') => { i18n.changeLanguage(lng); Cookies.set('i18next', lng); };

  // 主题切换器：当前配色在最左(橙=轮盘,其余=圆点),竖线分隔,再排其余三色
  const others = THEME_ORDER.filter((x) => x !== theme);
  const swatch = (th: Theme, size: number) =>
    th === 'orange'
      ? <img src={themeWheel} alt="" style={{ width: size, height: size, display: 'block' }} />
      : <span style={{ width: size, height: size, borderRadius: '50%', background: THEME[th].dot, display: 'block' }} />;

  const pillStyle = (bg: string): React.CSSProperties => ({
    position: 'absolute', top: 12, width: 75, height: 24, borderRadius: 12, background: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, color: '#000', border: 0, cursor: 'pointer',
  });

  return (
    <div
      style={{
        width: STG_W, height: STG_H, transform: `scale(${scale})`, transformOrigin: 'center',
        position: 'relative', flex: '0 0 auto',
      }}
    >
      {/* 左侧卡片：品牌 + Hero + 装饰(overflow 裁切) */}
      <div style={{ position: 'absolute', left: CARD.left, top: CARD.top, width: CARD.width, height: CARD.height, borderRadius: CARD.radius, background: colors.cardBg, overflow: 'hidden' }}>
        {/* 装饰(位于文字下层)：单色形状用 CSS mask 上色,颜色按主题查表 */}
        {DECOR.map((d) => {
          const mask = `url(/images/login/deco-${d.key}.svg) no-repeat center / 100% 100%`;
          return (
            <div key={d.key} aria-hidden="true" style={{ position: 'absolute', left: d.left, top: d.top, width: d.width, height: d.height, backgroundColor: DECOR_COLORS[theme][d.key], WebkitMask: mask, mask }} />
          );
        })}
        {/* 品牌：logo + 易學平台 */}
        <div style={{ position: 'absolute', left: 22, top: 28, display: 'flex', alignItems: 'center', gap: 7 }}>
          <img src="/images/login/logo.png" alt="logo" style={{ height: 18, width: 'auto' }} draggable={false} />
          <span style={{ fontFamily: '"Inter", "MiSans", system-ui, sans-serif', fontSize: 16, fontWeight: 600, color: '#000', letterSpacing: '-0.01em' }}>易學平台</span>
        </div>
        {/* Hero 标题 */}
        <h1 style={{ position: 'absolute', left: 24, top: 87, margin: 0, fontSize: 30, fontWeight: 500, lineHeight: '32px', color: '#000', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
          {t('welcome.hero-line-1')}<br />{t('welcome.hero-line-2')}
        </h1>
      </div>

      {/* 顶栏胶囊：關於我們 / 活動介紹 */}
      <button type="button" onClick={() => navigate(ROUTE.ABOUT)} style={{ ...pillStyle(colors.pillAbout), left: 412 }} className="hover:brightness-[0.97]">
        {t('login.about-us')}
      </button>
      <CompetitionRulesDialog>
        <button type="button" style={{ ...pillStyle(colors.pillActivity), left: 493 }} className="hover:brightness-[0.97]">
          {t('login.activities')}
        </button>
      </CompetitionRulesDialog>

      {/* 主题切换器 */}
      <div style={{ position: 'absolute', left: 601, top: 12, height: 24, display: 'flex', alignItems: 'center', gap: 7, padding: '0 7px', borderRadius: 12, background: '#FFFFFF' }}>
        <button type="button" aria-label={`current theme ${theme}`} style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}>
          {swatch(theme, 15)}
        </button>
        <span style={{ width: 1, height: 15, background: '#F3F3F4' }} />
        {others.map((th) => (
          <button key={th} type="button" onClick={() => setTheme(th)} aria-label={`switch to ${th} theme`} className="transition-transform hover:scale-110" style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 0 }}>
            {swatch(th, 15)}
          </button>
        ))}
      </div>

      {/* 语言切换 */}
      <div style={{ position: 'absolute', left: 723, top: 12, width: 109, height: 24, display: 'flex', alignItems: 'center', gap: 4, padding: 2, borderRadius: 12, background: colors.chip }}>
        <button type="button" onClick={() => setLang('en')} style={{ flex: '1 1 0', height: 20, borderRadius: 10, background: isEn ? '#FFF' : 'transparent', color: '#000', fontSize: 12, fontWeight: isEn ? 500 : 400, border: 0, cursor: 'pointer' }}>English</button>
        <button type="button" onClick={() => setLang('zh-HK')} style={{ flex: '1 1 0', height: 20, borderRadius: 10, background: !isEn ? '#FFF' : 'transparent', color: '#000', fontSize: 12, fontWeight: !isEn ? 500 : 400, border: 0, cursor: 'pointer' }}>繁體</button>
      </div>

      {/* 右侧表单(各页自行在舞台内 absolute 定位) */}
      {children}

      {/* 右下角机构 Logo + 版权小字：参考 PC 端(logo 行下方居中一行 #D1D5DB)。
          844 稿 logo 本贴底(距底 12px),为给版权腾位,logo 组整体上移、与版权一起底部对齐。 */}
      <div style={{ position: 'absolute', left: 646, top: 351, width: 186, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
          <img src="/images/login/ugc-logo.png" alt="University Grants Committee" style={{ height: 17, width: 'auto' }} />
          <img src="/images/login/footer-illustration.svg" alt="The Education University of Hong Kong" style={{ height: 17, width: 'auto' }} />
        </div>
        <div style={{ fontSize: 8, color: '#D1D5DB', whiteSpace: 'nowrap', textAlign: 'center' }}>
          {t('login.copyright')}
        </div>
      </div>
    </div>
  );
}
